import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, TrendingUp, XCircle, X, Zap } from 'lucide-react'
import { useAppSelector } from '@/store'
import { selectQuota, selectQuotaRevalidated } from '@/store/slices/authSlice'
import {
    useQuotaNotifications,
    dismissQuotaBanner,
    type QuotaSeverity,
    type QuotaStatus,
} from '@/hooks/useQuotaNotifications'

// ── Severity → visual config ──────────────────────────────────────

const SEVERITY_STYLES: Record<
    QuotaSeverity,
    {
        bg: string
        border: string
        iconBg: string
        iconColor: string
        title: string
        desc: string
        btn: string
        btnHover: string
        icon: React.ElementType
    }
> = {
    info: {
        bg: 'bg-[#EBF5FF]',
        border: 'border-[#BFDBFE]',
        iconBg: 'bg-[#DBEAFE]',
        iconColor: 'text-[#3B82F6]',
        title: 'text-[#1E3A5F]',
        desc: 'text-[#5B8DB8]',
        btn: 'bg-[#3B82F6] text-white',
        btnHover: 'hover:bg-[#2563EB]',
        icon: TrendingUp,
    },
    warning: {
        bg: 'bg-[#FFFBEB]',
        border: 'border-[#FDE68A]',
        iconBg: 'bg-[#FEF3C7]',
        iconColor: 'text-[#D97706]',
        title: 'text-[#78350F]',
        desc: 'text-[#B45309]',
        btn: 'bg-[#D97706] text-white',
        btnHover: 'hover:bg-[#B45309]',
        icon: AlertTriangle,
    },
    critical: {
        bg: 'bg-[#FEF2F2]',
        border: 'border-[#FECACA]',
        iconBg: 'bg-[#FEE2E2]',
        iconColor: 'text-[#DC2626]',
        title: 'text-[#7F1D1D]',
        desc: 'text-[#B91C1C]',
        btn: 'bg-[#DC2626] text-white',
        btnHover: 'hover:bg-[#B91C1C]',
        icon: XCircle,
    },
}

// ── Single banner row ──────────────────────────────────────────────

interface BannerRowProps {
    status: QuotaStatus
    onDismiss?: () => void
}

const BannerRow: React.FC<BannerRowProps> = ({ status, onDismiss }) => {
    const navigate = useNavigate()
    const { activeThreshold, usagePercent, used, limit } = status

    if (!activeThreshold) return null

    const styles = SEVERITY_STYLES[activeThreshold.severity]
    const Icon = styles.icon
    const canDismiss = activeThreshold.percent < 100 && onDismiss

    return (
        <div
            className={`w-full ${styles.bg} border ${styles.border} rounded-xl px-5 py-3 flex items-center justify-between gap-4`}
        >
            <div className="flex items-center gap-3 min-w-0">
                <div
                    className={`w-7 h-7 rounded-lg ${styles.iconBg} flex items-center justify-center flex-shrink-0`}
                >
                    <Icon className={`w-3.5 h-3.5 ${styles.iconColor}`} />
                </div>
                <div className="min-w-0">
                    <p className={`text-[13px] font-[550] ${styles.title}`}>
                        {activeThreshold.title}
                    </p>
                    <p className={`text-[12px] ${styles.desc} mt-0.5`}>
                        {activeThreshold.description}
                        <span className="ml-1 opacity-75">
                            ({used.toLocaleString()} / {limit.toLocaleString()} responses
                            &middot; {Math.round(usagePercent)}%)
                        </span>
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                {activeThreshold.showUpgrade && (
                    <button
                        onClick={() => navigate('/billing-plans')}
                        className={`h-8 px-4 rounded-lg text-[12px] font-[600] ${styles.btn} ${styles.btnHover} transition-all duration-150 shadow-sm flex items-center gap-1.5`}
                    >
                        <Zap className="w-3 h-3" />
                        Upgrade Plan
                    </button>
                )}

                {canDismiss && (
                    <button
                        onClick={onDismiss}
                        className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-black/5 transition-colors"
                        aria-label="Dismiss"
                    >
                        <X className={`w-3.5 h-3.5 ${styles.title}`} />
                    </button>
                )}
            </div>
        </div>
    )
}

// ── Main component ────────────────────────────────────────────────

const QuotaUsageBanner: React.FC = () => {
    const [localDismissedId, setLocalDismissedId] = useState<string | null>(null)
    const quota = useAppSelector(selectQuota)
    const quotaRevalidated = useAppSelector(selectQuotaRevalidated)
    const status = useQuotaNotifications(quota)

    // Only suppress banner if the SAME threshold was dismissed this session.
    // When a higher threshold kicks in (e.g. 50% → 80%), show it again.
    const isLocalDismissed = localDismissedId != null && localDismissedId === status.activeThreshold?.id

    // Don't render until /auth/me has returned fresh quota data this session.
    // Prevents stale persisted quota from flashing the wrong banner on refresh.
    if (!quotaRevalidated) return null

    if (!status.activeThreshold || status.isDismissed || isLocalDismissed) return null

    const handleDismiss = () => {
        if (status.dismissKey && status.activeThreshold) {
            dismissQuotaBanner(status.dismissKey, status.activeThreshold.id)
        }
        setLocalDismissedId(status.activeThreshold?.id ?? null)
    }

    const canDismiss = status.activeThreshold.percent < 100

    return (
        <div className="w-full">
            <BannerRow status={status} onDismiss={canDismiss ? handleDismiss : undefined} />
        </div>
    )
}

export default QuotaUsageBanner
